import type { JobSnapshot } from "~/types/media-subtitle";
import type { SubtitleOrientation, SubtitleRegion } from "~/utils/hardsub-region";

/*
 * Browser-side upload + job-creation helpers for the video-to-subtitle
 * platform. Shared by the single-job workflow and the batch queue, so nothing
 * here touches component state: progress goes through callbacks and errors are
 * mapped to user-facing copy by the caller's `UploadErrorMapper`.
 */

export type UploadedPart = { partNumber: number; etag: string };

export type ResumableUploadMeta = {
  name: string;
  size: number;
  lastModified: number;
  type: string;
  partSize: number;
  totalParts: number;
  createdAt: number;
};

export type ResumableUploadServerState = {
  uploadId: string | null;
  uploadStatus: "idle" | "processing" | "done" | "error";
  partSize: number;
  parts: Array<{ partNumber: number; etag: string; size: number }>;
};

export type ResumableUploadCandidate = {
  jobId: string;
  meta: ResumableUploadMeta;
  state: ResumableUploadServerState;
};

export type UploadProgress = { loaded: number; total: number; percent: number };

export type UploadErrorMapper = {
  /** Generic "Unable to upload the media file." copy. */
  uploadFailed: () => string;
  /** Maps a raw server body (JSON or text) to friendly copy. */
  fromText: (rawText: string) => string;
  /** Reads a failed response and maps it to friendly copy. */
  fromResponse: (response: Response) => Promise<string>;
};

type UploadOptions = {
  signal: AbortSignal;
  errors: UploadErrorMapper;
  onProgress?: (progress: UploadProgress) => void;
};

const RESUMABLE_KEY_PREFIX = "vts:upload:";
const RESUMABLE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const UPLOAD_RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000];
const DEFAULT_PART_SIZE = 8 * 1024 * 1024;
const UPLOAD_CONCURRENCY = 3;

/** Error whose message is already safe to show to the user. */
export function createSafeClientError(message: string) {
  return Object.assign(new Error(message), { isSafeClientMessage: true as const });
}

export function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function resumableStorageKey(jobId: string) {
  return `${RESUMABLE_KEY_PREFIX}${jobId}`;
}

export function loadResumableUploadMeta(jobId: string): ResumableUploadMeta | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(resumableStorageKey(jobId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ResumableUploadMeta;
    if (
      !parsed
      || typeof parsed.name !== "string"
      || typeof parsed.size !== "number"
      || typeof parsed.lastModified !== "number"
      || typeof parsed.partSize !== "number"
      || typeof parsed.createdAt !== "number"
    ) {
      window.localStorage.removeItem(resumableStorageKey(jobId));
      return null;
    }
    if (Date.now() - parsed.createdAt > RESUMABLE_TTL_MS) {
      window.localStorage.removeItem(resumableStorageKey(jobId));
      return null;
    }
    return parsed;
  } catch {
    window.localStorage.removeItem(resumableStorageKey(jobId));
    return null;
  }
}

function saveResumableUploadMeta(jobId: string, meta: ResumableUploadMeta) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(resumableStorageKey(jobId), JSON.stringify(meta));
  } catch {
    // Quota or private mode - resume becomes best-effort, regular upload still works.
  }
}

export function clearResumableUploadMeta(jobId: string) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(resumableStorageKey(jobId));
}

function isRetryableUploadError(error: unknown) {
  if (isAbortError(error)) {
    return false;
  }
  const status = (error as { status?: number } | null | undefined)?.status;
  if (typeof status === "number") {
    return status >= 500 || status === 408 || status === 425 || status === 429;
  }
  return true;
}

function delayWithAbort(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort);
  });
}

function isNetworkError(error: unknown) {
  if (isAbortError(error)) return false;
  const status = (error as { status?: number } | null | undefined)?.status;
  return typeof status !== "number" || status === 0;
}

function waitForOnline(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (typeof navigator === "undefined" || navigator.onLine) {
      resolve();
      return;
    }
    if (signal.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }
    const cleanup = () => {
      window.removeEventListener("online", onOnline);
      signal.removeEventListener("abort", onAbort);
    };
    const onOnline = () => {
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    window.addEventListener("online", onOnline);
    signal.addEventListener("abort", onAbort);
  });
}

function uploadPartXhr(
  jobId: string,
  partNumber: number,
  chunk: Blob,
  signal: AbortSignal,
  errors: UploadErrorMapper,
  onProgress: (loaded: number) => void,
): Promise<UploadedPart> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `/api/video-to-subtitle/platform/upload-part?jobId=${encodeURIComponent(jobId)}&partNumber=${partNumber}`;
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    };

    const cleanup = () => signal.removeEventListener("abort", onAbort);
    const onAbort = () => xhr.abort();
    signal.addEventListener("abort", onAbort, { once: true });

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const payload = JSON.parse(xhr.responseText) as UploadedPart;
          if (!payload?.etag) {
            reject(createSafeClientError(errors.uploadFailed()));
            return;
          }
          resolve({ partNumber, etag: payload.etag });
        } catch {
          reject(createSafeClientError(errors.uploadFailed()));
        }
        return;
      }
      const err = createSafeClientError(errors.fromText(xhr.responseText || "") || errors.uploadFailed());
      (err as { status?: number }).status = xhr.status;
      reject(err);
    };

    xhr.onerror = () => {
      cleanup();
      reject(createSafeClientError(errors.uploadFailed()));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };

    xhr.send(chunk);
  });
}

async function uploadPartWithRetry(
  jobId: string,
  partNumber: number,
  chunk: Blob,
  signal: AbortSignal,
  errors: UploadErrorMapper,
  onProgress: (loaded: number) => void,
): Promise<UploadedPart> {
  let attempt = 0;

  while (true) {
    if (signal.aborted) {
      throw new DOMException("Upload aborted", "AbortError");
    }

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await waitForOnline(signal);
    }

    try {
      onProgress(0);
      return await uploadPartXhr(jobId, partNumber, chunk, signal, errors, onProgress);
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (!isRetryableUploadError(error)) throw error;

      // Offline drops don't count toward the retry budget - wait for the
      // browser to come back online and try again.
      if (isNetworkError(error) && typeof navigator !== "undefined" && !navigator.onLine) {
        await waitForOnline(signal);
        continue;
      }

      if (attempt >= UPLOAD_RETRY_DELAYS.length) throw error;
      await delayWithAbort(UPLOAD_RETRY_DELAYS[attempt], signal);
      attempt += 1;
    }
  }
}

async function runChunkedUpload(options: UploadOptions & {
  jobId: string;
  file: File;
  partSize: number;
  queue: number[];
  partLoaded: Map<number, number>;
  completedParts: UploadedPart[];
}): Promise<JobSnapshot> {
  const { jobId, file, partSize, queue, partLoaded, completedParts, signal, errors, onProgress } = options;

  const recomputeProgress = () => {
    let loaded = 0;
    partLoaded.forEach((value) => {
      loaded += value;
    });
    if (loaded > file.size) loaded = file.size;
    // Capped at 99% until finalize confirms the upload.
    const percent = file.size > 0
      ? Math.max(1, Math.min(99, Math.round((loaded / file.size) * 100)))
      : 0;
    onProgress?.({ loaded, total: file.size, percent });
  };
  recomputeProgress();

  const worker = async () => {
    while (queue.length > 0) {
      if (signal.aborted) {
        throw new DOMException("Upload aborted", "AbortError");
      }
      const partNumber = queue.shift()!;
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      const uploaded = await uploadPartWithRetry(
        jobId,
        partNumber,
        chunk,
        signal,
        errors,
        (loaded) => {
          partLoaded.set(partNumber, loaded);
          recomputeProgress();
        },
      );
      partLoaded.set(partNumber, end - start);
      recomputeProgress();
      completedParts.push(uploaded);
    }
  };

  const concurrency = Math.min(UPLOAD_CONCURRENCY, Math.max(1, queue.length));
  if (queue.length > 0) {
    try {
      await Promise.all(Array.from({ length: concurrency }, () => worker()));
    } catch (error) {
      queue.length = 0;
      throw error;
    }
  }

  const finalizeResponse = await fetch("/api/video-to-subtitle/platform/upload-finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jobId,
      parts: completedParts.slice().sort((a, b) => a.partNumber - b.partNumber),
    }),
    signal,
  });

  if (!finalizeResponse.ok) {
    throw createSafeClientError(await errors.fromResponse(finalizeResponse));
  }

  onProgress?.({ loaded: file.size, total: file.size, percent: 100 });

  const data = await finalizeResponse.json() as { job: JobSnapshot };
  return data.job;
}

/**
 * Uploads `file` to an already created job in parts, then finalizes it. The
 * resume record in localStorage lets a reload pick the upload up again.
 */
export async function uploadFileChunked(
  jobId: string,
  file: File,
  options: UploadOptions,
): Promise<JobSnapshot> {
  const { signal, errors } = options;
  const initResponse = await fetch("/api/video-to-subtitle/platform/upload-init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jobId }),
    signal,
  });

  if (!initResponse.ok) {
    throw createSafeClientError(await errors.fromResponse(initResponse));
  }

  const initPayload = await initResponse.json() as {
    uploadId: string;
    partSize: number;
    maxParts: number;
  };

  const serverPartSize = Math.max(initPayload.partSize || DEFAULT_PART_SIZE, 5 * 1024 * 1024);
  const maxParts = Math.max(1, initPayload.maxParts || 10000);
  const partSize = Math.max(serverPartSize, Math.ceil(file.size / maxParts) || serverPartSize);
  const totalParts = Math.max(1, Math.ceil(file.size / partSize));

  saveResumableUploadMeta(jobId, {
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    type: file.type || "application/octet-stream",
    partSize,
    totalParts,
    createdAt: Date.now(),
  });

  const job = await runChunkedUpload({
    ...options,
    jobId,
    file,
    partSize,
    queue: Array.from({ length: totalParts }, (_, i) => i + 1),
    partLoaded: new Map<number, number>(),
    completedParts: [],
  });

  clearResumableUploadMeta(jobId);
  return job;
}

/** Uploads only the parts the server does not have yet, then finalizes. */
export async function resumeUpload(
  jobId: string,
  file: File,
  meta: ResumableUploadMeta,
  serverState: ResumableUploadServerState,
  options: UploadOptions,
): Promise<JobSnapshot> {
  const partSize = Math.max(meta.partSize, serverState.partSize || meta.partSize);
  const totalParts = Math.max(1, Math.ceil(file.size / partSize));

  const partLoaded = new Map<number, number>();
  const completedParts: UploadedPart[] = [];
  const completedSet = new Set<number>();
  for (const part of serverState.parts) {
    if (!Number.isInteger(part.partNumber) || part.partNumber < 1 || part.partNumber > totalParts) continue;
    completedSet.add(part.partNumber);
    completedParts.push({ partNumber: part.partNumber, etag: part.etag });
    const start = (part.partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    partLoaded.set(part.partNumber, Math.max(0, Math.min(end - start, part.size || end - start)));
  }

  const queue: number[] = [];
  for (let n = 1; n <= totalParts; n++) {
    if (!completedSet.has(n)) queue.push(n);
  }

  const job = await runChunkedUpload({
    ...options,
    jobId,
    file,
    partSize,
    queue,
    partLoaded,
    completedParts,
  });

  clearResumableUploadMeta(jobId);
  return job;
}

/** Returns the unfinished upload of `jobId` when both browser and server still have it. */
export async function detectResumableUpload(jobId: string): Promise<ResumableUploadCandidate | null> {
  const meta = loadResumableUploadMeta(jobId);
  if (!meta) return null;

  try {
    const response = await fetch(
      `/api/video-to-subtitle/platform/upload-state?jobId=${encodeURIComponent(jobId)}`,
    );
    if (!response.ok) {
      if (response.status === 404 || response.status === 410) {
        clearResumableUploadMeta(jobId);
      }
      return null;
    }
    const state = await response.json() as ResumableUploadServerState;
    if (!state.uploadId || state.uploadStatus === "done" || state.uploadStatus === "error") {
      clearResumableUploadMeta(jobId);
      return null;
    }
    return { jobId, meta, state };
  } catch {
    return null;
  }
}

/** Best-effort abort of a job's multipart upload (also releases its credit hold). */
export function abortUpload(jobId: string) {
  clearResumableUploadMeta(jobId);
  return fetch("/api/video-to-subtitle/platform/upload-abort", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jobId }),
  }).then(() => undefined, () => undefined);
}

/**
 * Cancels a job at any stage and releases its credit hold. Returns the job's
 * snapshot afterwards (still "ready" if it finished before the cancel landed).
 */
export async function cancelJob(jobId: string, errors: UploadErrorMapper) {
  clearResumableUploadMeta(jobId);
  const data = await postJson<{ job: JobSnapshot }>(
    "/api/video-to-subtitle/platform/cancel",
    { jobId },
    errors,
  );
  return data.job;
}

async function postJson<T>(url: string, body: unknown, errors: UploadErrorMapper, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw createSafeClientError(await errors.fromResponse(response));
  }
  return await response.json() as T;
}

/** Optional time range sent with a job; the server bills its length only. */
export type ClipRequest = {
  startSeconds: number;
  endSeconds: number;
  timestampMode: "original" | "zero";
};

export type CreateUploadJobInput = {
  fileName: string;
  contentType: string;
  fileSize: number;
  durationSeconds: number;
  title: string;
  targetLanguage: string;
  clip?: ClipRequest;
  /** Which page is creating the job; defaults to "subtitle" on the server. */
  tool?: "subtitle" | "summary" | "ocr";
  /** Hardsub OCR only: the frame rectangle holding the burned-in subtitles. */
  ocrRegion?: SubtitleRegion;
  ocrOrientation?: SubtitleOrientation;
  /** Hardsub OCR only: script/language hint ("auto" or empty = detect). */
  sourceLanguage?: string;
};

/** Creates (and reserves credits for) a file-upload job. */
export function createUploadJob(input: CreateUploadJobInput, options: { errors: UploadErrorMapper; signal?: AbortSignal }) {
  return postJson<{ jobId: string; estimatedCost?: number }>(
    "/api/video-to-subtitle/platform/create",
    input,
    options.errors,
    options.signal,
  );
}

export type ResolvedMediaUrl = {
  resolved: string;
  signature: string;
  preview?: { title: string; mediaKind: string; durationSeconds: number; estimatedCost: number };
};

/** Resolves a media page URL into a signed, short-lived ticket. */
export function resolveMediaUrl(
  input: { sourceUrl: string; title?: string; durationSeconds?: number; clip?: ClipRequest; tool?: string },
  options: { errors: UploadErrorMapper; signal?: AbortSignal },
) {
  return postJson<ResolvedMediaUrl>(
    "/api/video-to-subtitle/url/resolve",
    {
      sourceUrl: input.sourceUrl,
      title: input.title || "",
      durationSeconds: Number(input.durationSeconds || 0),
      ...(input.clip ? { clip: input.clip } : {}),
      ...(input.tool ? { tool: input.tool } : {}),
    },
    options.errors,
    options.signal,
  );
}

/** Creates (and reserves credits for) a from-URL job from a resolve ticket. */
export async function createUrlJob(
  input: {
    resolved: string;
    signature: string;
    targetLanguage: string;
    clip?: ClipRequest;
    tool?: string;
    ocrRegion?: SubtitleRegion;
    ocrOrientation?: SubtitleOrientation;
    sourceLanguage?: string;
  },
  options: { errors: UploadErrorMapper; signal?: AbortSignal },
) {
  const data = await postJson<{ job: JobSnapshot }>(
    "/api/video-to-subtitle/url/create",
    input,
    options.errors,
    options.signal,
  );
  return data.job;
}

/** Reads a media file's duration (seconds) from its metadata; 0 when unreadable. */
export function loadMediaDuration(file: File): Promise<number> {
  const objectUrl = URL.createObjectURL(file);

  return new Promise<number>((resolve) => {
    const media = document.createElement(file.type.startsWith("video/") ? "video" : "audio");

    media.preload = "metadata";
    media.onloadedmetadata = () => {
      const seconds = Number.isFinite(media.duration) ? media.duration : 0;
      URL.revokeObjectURL(objectUrl);
      resolve(seconds);
    };
    media.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(0);
    };
    media.src = objectUrl;
  });
}
